import type { ChannelMemberResponse } from "stream-chat";

type Options = {
  /**
   * Replaces every member's identity with the anonymous placeholder, dropping the avatar too.
   * Set for a FROZEN channel (ADR-0008): a frozen DM means the pair is cut off, and neither side
   * is told why — so neither side sees who the counterpart is.
   */
  isAnonymized?: boolean;
  anonymousName: string;
};

/**
 * Replaces each member's Stream name with the one THIS viewer may see (ADR-0007), or with the
 * anonymous placeholder when the channel is frozen (ADR-0008).
 *
 * The single implementation of that swap. Every surface that reads a member name — the channel
 * header, the list preview, the member list, the search filter — must go through here: Stream
 * carries only the disclosable name (@handle for a private persona), so a surface reading
 * `member.user.name` directly shows the handle to people entitled to the real name. Two bugs of
 * exactly that shape were shipped before this helper existed.
 */
export const resolveMemberNames = (
  members: Array<ChannelMemberResponse>,
  /** Persona id → the name this viewer may show (useDisclosedNamesMapQuery). */
  disclosedNames: ReadonlyMap<string, string> | undefined,
  { isAnonymized, anonymousName }: Options,
): Array<ChannelMemberResponse> => {
  if (isAnonymized) {
    // Anonymisation wins over disclosure: being entitled to someone's real name does not survive
    // the channel being cut off. The avatar goes too, so the initial-letter fallback renders the
    // generic placeholder.
    return members.map((member) =>
      member.user
        ? {
            ...member,
            user: { ...member.user, name: anonymousName, image: undefined },
          }
        : member,
    );
  }

  if (!disclosedNames?.size) {
    return members;
  }

  return members.map((member) => {
    const disclosedName =
      member.user_id === undefined
        ? undefined
        : disclosedNames.get(member.user_id);

    if (!member.user || !disclosedName) {
      return member;
    }

    return { ...member, user: { ...member.user, name: disclosedName } };
  });
};
